import * as React from 'react'
import { VotePrompt } from './types'
import { xhr } from '../../../xhr'
import { Spacer } from '../../elementals/Spacer'
import { Group } from '../../elementals/Group'
import { ContentBox } from '../../elementals/ContentBox'
import { Button } from '../../elementals/Button'
import { Radio, Checkbox } from '../../elementals/Chooser'
import { Plus } from '../../elementals/Icons/Plus'
import { Close } from '../../elementals/Icons/Close'
import { Check } from '../../elementals/Icons/Check'
import { formatDateForInput, formatTimeForInput } from '../../../utils/date'
import { translate } from '../../../utils/translations'
import * as translations from './translations'

type Props = {
  setIsCreating: React.Dispatch<React.SetStateAction<boolean>>
  setPrompts: React.Dispatch<React.SetStateAction<Array<VotePrompt>>>
}
export const CreatePrompt = ({ setIsCreating, setPrompts }: Props) => {
  const now = new Date()

  const [promptText, setPromptText] = React.useState('')
  const [promptBy, setPromptBy] = React.useState(getRandomName())
  const [answers, setAnswers] = React.useState<Array<string>>([])
  const [isPublic, setIsPublic] = React.useState(false)
  const [currentAnswer, setCurrentAnswer] = React.useState('')
  const [closedAtDate, setClosedAtDate] = React.useState<string>(() => {
    const nextWeek = new Date(now)
    nextWeek.setDate(nextWeek.getDate() + 7)
    return formatDateForInput(nextWeek)
  })
  const [closedAtTime, setClosedAtTime] = React.useState<string>(() => {
    const nextMinutes = new Date(now)
    nextMinutes.setMinutes(Math.ceil(nextMinutes.getMinutes() / 5) * 5)
    return formatTimeForInput(nextMinutes)
  })

  const submitPrompt = () => {
    if (
      promptBy.trim() &&
      promptText.trim() &&
      (answers.length > 1 || (answers.length > 0 && currentAnswer.trim()))
    ) {
      xhr
        .post(
          '/api/vote',
          JSON.stringify({
            isPublic,
            createdBy: promptBy,
            promptText,
            answers: currentAnswer.trim()
              ? answers.concat(currentAnswer)
              : answers,
            closedAt: closedAtDate ? closedAtDate + 'T' + closedAtTime : null,
          }),
        )
        .then(({ data }) => {
          setPrompts(prev => data.concat(prev))
          setIsCreating(false)
        })
    }
  }

  const submitAnswer = () => {
    if (currentAnswer.trim()) {
      setAnswers(prev => prev.concat(currentAnswer))
      setCurrentAnswer('')
    }
  }

  return (
    <ContentBox
      title={
        <Spacer space={8}>
          <input
            type="text"
            value={promptBy}
            placeholder="Your name"
            onChange={event => setPromptBy(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && submitPrompt()}
            style={{ position: 'relative', flexGrow: 1 }}
          />
          <Button onClick={() => setIsCreating(false)}>
            <Close />
          </Button>
        </Spacer>
      }
    >
      <Spacer orientation="vertical" space={8}>
        <Spacer>
          <input
            type="text"
            value={promptText}
            placeholder={translate({
              translations,
              path: ['createPrompt', 'promptPlaceholder'],
            })}
            onChange={event => setPromptText(event.target.value)}
            onKeyDown={event => event.key === 'Enter' && submitPrompt()}
            style={{ position: 'relative', width: '100%' }}
          />
        </Spacer>
        {answers.length > 0 && (
          <ul>
            {answers.map((answer, index) => (
              <li key={index}>
                <Radio name="answers">{answer}</Radio>
              </li>
            ))}
          </ul>
        )}
        <Spacer space={8} orientation="vertical">
          <Group>
            <input
              type="text"
              value={currentAnswer}
              placeholder={translate({
                translations,
                path: ['createPrompt', 'answerPlaceholder'],
                passed: answers.length + 1,
              })}
              onChange={event => setCurrentAnswer(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && submitAnswer()}
              style={{ position: 'relative', flexGrow: 1 }}
            />
            <Button onClick={submitAnswer}>
              <Plus />
            </Button>
          </Group>
          <Spacer orientation="vertical">
            <Spacer space={16}>
              <div>
                {translate({
                  translations,
                  path: ['createPrompt', 'invalidation'],
                })}
              </div>
              <Group style={{ position: 'relative', flexGrow: 1 }}>
                <input
                  type="date"
                  value={closedAtDate}
                  onChange={event => setClosedAtDate(event.target.value)}
                  min={formatDateForInput(now)}
                  pattern="\d{4}-\d{2}-\d{2}"
                  style={{ position: 'relative', flexGrow: 1 }}
                />
                <input
                  type="time"
                  value={closedAtTime}
                  onChange={event =>
                    setClosedAtTime(event.target.value || '00:00')
                  }
                  min={formatTimeForInput(now)}
                  pattern="\d{2}:\d{2}"
                  style={{ position: 'relative', flexGrow: 1 }}
                />
              </Group>
            </Spacer>
          </Spacer>
          <Spacer space="all">
            <Checkbox
              title={translate({
                translations,
                path: ['createPrompt', 'publicInfo'],
              })}
              checked={isPublic}
              onChange={event => setIsPublic(event.target.checked)}
            >
              {translate({
                translations,
                path: ['createPrompt', 'public'],
              })}
            </Checkbox>
            <Button data-type="primary" onClick={() => submitPrompt()}>
              <Check />
            </Button>
          </Spacer>
        </Spacer>
      </Spacer>
    </ContentBox>
  )
}

// prettier-ignore
const lastnames = ['Hoxha','Prifti','Shehu','Hovhannisyan','Harutyunyan','Sargsyan','Gruber','Huber','Bauer','Wagner','Müller','Pichler','Mammadov','Aliyev','Huseynov','Ivanoŭ','Kazloŭ','Kavalioŭ','Peeters','Janssens','Maes','Jacobs','Mertens','Willems','Claes','Goossens','Wouters','De Smet','Hodžić','Čengić','Delić','Kovačević','Subotić','Savić','Popović','Jovanović','Petrović','Ivanov','Georgiev','Dimitrov','Petrov','Nikolov','Horvat','Kovačević','Babić','Marić','Jurić','Novák','Svoboda','Novotný','Dvořák','Černý','Procházka','Nielsen','Jensen','Hansen','Pedersen','Andersen','Christensen','Larsen','Sørensen','Rasmussen','Jørgensen','Petersen','Madsen','Kristensen','Olsen','Thomsen','Christiansen','Poulsen','Tamm','Saar','Sepp','Mägi','Kask','Kukk','Rebane','Ilves','Pärn','Koppel','Ivanov','Smirnov','Vassiljev','Petrov','Kuznetsov','Mihhailov','Pavlov','Semjonov','Andrejev','Aleksejev','Korhonen','Virtanen','Mäkinen','Nieminen','Mäkelä','Hämäläinen','Laine','Heikkinen','Koskinen','Järvinen','Lehtonen','Lehtinen','Saarinen','Salminen','Martin','Bernard','Dubois','Thomas','Robert','Richard','Petit','Durand','Leroy','Moreau','Simon','Laurent','Lefebvre','Michel','Garcia','David','Bertrand','Roux','Vincent','Fournier','Beridze','Mamedovi','Alievi','Müller','Schmidt','Schneider','Fischer','Meyer','Weber','Schulz','Wagner','Becker','Hoffmann','Samaras','Papoutsis','Kritikos','Aivaliotis','Kontos','Mytaras','Koufos','Nagy','Kovács','Tóth','Szabó','Horváth','Varga','Kiss','Molnár','Németh','Farkas','Blöndal','Thorarensen','Hansen','Olsen','Andersen','Petersen','Möller','Nielsen','Waage','Bergmann','Murphy','Kelly','Sullivan','Walsh','Smith', "O'Brien",'Byrne','Ryan', "O'Connor", "O'Neill",'Reilly','Doyle','McCarthy','Gallagher','Doherty','Kennedy','Lynch','Murray','Rossi','Russo','Ferrari','Esposito','Bianchi','Romano','Colombo','Bruno','Ricci','Greco','Marino','Gallo','De Luca','Conti','Costa','Mancini','Giordano','Rizzo','Lombardi','Barbieri','Moretti','Krasniqi','Gashi','Berisha','Bērziņš','Kalniņš','Ozoliņš','Jansons','Ozols','Kazlauskas','Jankauskas','Petrauskas','Stankevičius','Vasiliauskas','Butkus','Schmit','Muller','Weber','Hoffmann','Wagner','Thill','Schmitz','Schroeder','Borg','Camilleri','Vella','Farrugia','Rusu','Ceban','Ciobanu','Țurcan','Cebotari','Popović','Marković','Vujović','Radović','Ivanović','De Jong','Jansen','De Vries','Van den Berg','Van Dijk','Van Dyk','Bakker','Janssen','Visser','Schmit','Meijer','Meyer','De Boer','Mulder','De Groot','Bos','Vos','Peters','Hendriks','Van Leeuwen','Andov','Angelov','Bogdanov','Bozinov','Brankov','Damcevski','Hansen','Johansen','Olsen','Larsen','Andersen','Pedersen','Nilsen','Kristiansen','Jensen','Karlsen','Nowak','Kowalski','Wiśniewski','Wójcik','Kowalczyk','Kamiński','Lewandowski','Silva','Santos','Ferreira','Pereira','Oliveira','Costa','Rodrigues','Martins','Jesus','Sousa','Fernandes','Gonçalves','Gomes','Lopes','Marques','Popa','Popescu','Pop','Radu','Dumitru','Stan','Stoica','Gheorghe','Matei','Ciobanu','Smirnov','Ivanov','Kuznetsov','Popov','Sokolov','Lebedev','Kozlov','Novikov','Morozov','Petrov','Volkov','Horváth','Kováč','Varga','Tóth','Nagy','Baláž','Szabó','García','Fernández','González','Rodríguez','López','Martínez','Sánchez','Pérez','Martín','Gómez','Ruiz','Hernández','Jiménez','Díaz','Álvarez','Moreno','Muñoz','Andersson','Johansson','Karlsson','Nilsson','Eriksson','Larsson','Olsson','Persson','Svensson','Gustafsson','Pettersson','Jonsson','Yılmaz','Kaya','Demir','Şahin','Çelik','Yıldız','Yıldırım','Öztürk','Aydın','Özdemir','Arslan','Melnyk','Shevchenko','Boyko','Kovalenko','Bondarenko','Tkachenko','Kovalchuk','Kravchenko','Smith','Jones','Taylor','Brown','Williams','Wilson','Johnson','Davies','Robinson','Wright','Thompson','Evans','Walker','White','Roberts']

// prettier-ignore
const firstnames = ['Noel','Joel','Mateo','Ergi','Luis','Aron','Marc','Eric','Jan','Daniel','Enzo','Ian','Davit','Narek','Gor','Hayk','Alex','Arman','Lukas','Maximilian','Jakob','David','Tobias','Paul','Artsiom','Mikhail','Maksim','Ivan','Roman','Aleksandr','Lucas','Louis','Noah','Nathan','Adam','Arthur','Lucas','Liam','Louis','Wout','Mathis','Lars','Ahmed','Daris','Amar','Davud','Adin','Hamza','Stefan','Luka','Lazar','Nikola','Pavle','David','Georgi','Aleksandar','Martin','Dimitar','Ivan','Nikolay','Luka','David','Ivan','Jakov','Marko','Petar','Andreas','Georgios','Konstantinos','Christos','Nikolaos','Jakub','Jan','Tomáš','David','Adam','Matyáš','William','Noah','Oscar','Lucas','Victor','Malthe','Oliver','George','Harry','Jack','Jacob','Noah','Rasmus','Robin','Oliver','Maksim','Robert','Martin','Leo','Elias','Oliver','Eino','Väinö','Eeli','Gabriel','Louis','Raphaël','Jules','Adam','Lucas','Giorgi','Nikoloz','Andria','Gabrieli','Luka','Saba','Ben','Jonas','Leon','Elias','Finn','Noah','Georgios','Ioannis','Konstantinos','Dimitrios','Nikolaos','Panagiotis','Bence','Máté','Levente','Ádám','Dávid','Dániel','Aron','Alexander','Viktor','Kristján','Jón','Guðmundur','Jack','James','Daniel','Conor','Seán','Adam','Leonardo','Francesco','Alessandro','Lorenzo','Mattia','Andrea','Roberts','Markuss','Artjoms','Ralfs','Gustavs','Maksims','Lukas','Matas','Nojus','Dominykas','Jokūbas','Emilis','Aleksandar','Zoran','Nikola','Goran','Dragan','Dejan','Daan','Noah','Sem','Lucas','Jesse','Finn','William','Oskar','Lucas','Mathias','Filip','Oliver','Antoni','Jakub','Jan','Szymon','Aleksander','Franciszek','João','Martim','Rodrigo','Santiago','Francisco','Afonso','Andrei','David','Alexandru','Gabriel','Mihai','Cristian','Alexander','Maxim','Artyom','Mikhail','Daniil','Ivan','Jakub','Adam','Samuel','Lukáš','Tim','Nik','Hugo','Daniel','Martín','Pablo','Alejandro','Lucas','Markel','Aimar','Jon','Ibai','Julen','Ander','Oscar','Lucas','William','Liam','Oliver','Hugo','Noah','Liam','Gabriel','Luca','Leon','Elias','Artem','Matviy','Maksym','David','Nikita','Mykhailo','Amelia','Ajla','Melisa','Amelija','Klea','Sara','Laia','Carlota','Emma','Lara','Nareh','Mari','Maneh','Milena','Ani','Anahit','Anna','Hannah','Sophia','Emma','Marie','Lena','Sofiya','Marya','Polina','Eva','Anna','Darya','Maria','Marie','Dominique','Martine','Nathalie','Anne','Amina','Merjem','Sara','Asja','Lamija','Ema','Viktoria','Maria','Nikol','Aleksandra','Gabriela','Daria','Mia','Lucija','Sara','Ana','Ema','Petra','Maria','Eleni','Androula','Georgia','Panagiota','Anna','Eliška','Tereza','Anna','Adéla','Natálie','Sofie','Ida','Emma','Alma','Ella','Sofia','Freja','Olivia','Amelia','Emily','Isla','Ava','Jessica','Sofia','Eliise','Maria','Sandra','Laura','Mia','Alisa','Aino','Aada','Sofia','Eevi','Olivia','Lilja','Emma','Louise','Jade','Alice','Chloé','Lina','Mariami','Barbare','Elene','Anastasia','Nino','Nia','Mia','Emma','Hannah','Sophia','Anna','Emilia','Maria','Eleni','Aikaterini','Vasiliki','Sophia','Angeliki','Hanna','Anna','Jázmin','Lili','Zsófia','Emma','Margrét','Anna','Emma','Ísabella','Eva','Hekla','Emily','Emma','Ava','Sophie','Amelia','Ella','Sofia','Giulia','Aurora','Alice','Ginevra','Emma','Sofija','Emīlija','Alise','Anna','Marta','Viktorija','Marija','Elena','Biljana','Vesna','Snezana','Violeta','Elena','Julia','Emma','Eliza','Catherine','Maya','Anna','Emma','Tess','Sophie','Julia','Zoë','Evi','Mila','Nora','Emma','Sara','Sofie','Sofia','Maja','Zuzanna','Julia','Lena','Maja','Hanna','Zofia','Maria','Leonor','Matilde','Beatriz','Carolina','Mariana','Maria','Elena','Ionana','Andreea','Sofia','Alexandra','Anastasia','Yelena','Olga','Natalia','Yekaterina','Anna','Milica','Jelena','Marija','Mirjana','Dragana','Snežana','Lucía','Martina','María','Sofía','Paula','Daniela','Ane','June','Nahia','Irati','Laia','Nora','Alice','Lilly','Maja','Elsa','Ella','Alicia','Sofiya','Anastasiya','Anna','Mariya','Alisa','Viktoriya']

const getRandomName = () =>
  firstnames[Math.floor(Math.random() * firstnames.length)] +
  ' ' +
  lastnames[Math.floor(Math.random() * lastnames.length)]
